import * as React from 'react';
import { ChangeEvent, useCallback, useEffect, useState } from 'react';
import { useGlobalStore } from '../utilities/GlobalState';
import {
    CustomerDistributorLevels,
    PriceConfiguratorAnswers,
    PriceConfiguratorQuestions,
    PriceConfiguratorAnswerProducts,
    PriceList,
} from '../interfaces/Interfaces';
import { AuthGet } from '../utilities/Routing/Requests';
import { toast } from 'react-toastify';
import { defaultTheme } from '../utilities/Utilites';
import {
    DataGridPro,
    DataGridProProps,
    gridFilteredSortedRowIdsSelector,
    GridRowsProp,
    GridRowId,
    GridColDef,
    GridFilterModel,
    GridColumnVisibilityModel,
    useGridApiRef,
    GridSortModel,
} from '@mui/x-data-grid-pro';
import {
    Box,
    Card,
    CardContent,
    Checkbox,
    Chip,
    Divider,
    FormControl,
    FormControlLabel,
    Grid,
    IconButton,
    Menu,
    MenuItem,
    TextField,
    ThemeProvider,
    Typography,
    Select,
    SelectChangeEvent,
    CircularProgress,
    Dialog,
    Button,
    Tooltip,
} from '@mui/material';
import {
    Download,
    Search,
    ArrowCircleDownOutlined,
    ArrowCircleUpOutlined,
    ViewWeekOutlined,
    SettingsBackupRestoreOutlined,
    Visibility,
    KeyboardBackspace,
} from '@mui/icons-material';
import PriceDialog from './QuestionAnswerDialog';

export const QuotesPage = () => {
    //Usestates//--------------------------------//
    //
    //Grid Usestate//
    const [detailPanelExpandedRowIds, setDetailPanelExpandedRowIds] = useState<
        GridRowId[]
    >([]);
    const [sortModel, setSortModel] = React.useState<GridSortModel>([
        {
            field: 'distributor_Type',
            sort: 'asc',
        },
    ]);

    const [loadingDataGrid, setLoadingDataGrid] = useState(false);
    const [searchColumnText, setColumnSearchText] = useState('');
    const [filterModel, setFilterModel] = useState<GridFilterModel>({
        items: [],
    });
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const openAnchor = Boolean(anchorEl);
    const [searchColumn, setSearchColumn] = useState<
        | 'distributor_Type'
        | 'product_Grouping'
        | 'product_Name'
        | 'material'
        | 'msrp'
        | 'discount_Percentage'
        | 'type_Level'
        | 'sales_Lead_Time'
        | 'available_Quantity'
        | 'sales_Published_Date'
    >('product_Name');

    const [columnVisibilityModel, setColumnVisibilityModel] =
        useState<GridColumnVisibilityModel>({
            distributor_Type: true,
            product_Grouping: true,
            product_Name: true,
            material: true,
            msrp: true,
            discount_Percentage: false,
            type_Level: false,
            sales_Lead_Time: true,
            available_Quantity: true,
            sales_Published_Date: false,
        });

    //Build & Price Usestate//
    const [questions, setQuestions] = useState<PriceConfiguratorQuestions[]>(
        [],
    );
    const [answers, setAnswers] = useState<PriceConfiguratorAnswers[]>([]);
    const [answerProducts, setAnswerProducts] = useState<
        PriceConfiguratorAnswerProducts[]
    >([]);
    const [selectedConfiguratorAnswer, setSelectedConfiguratorAnswer] =
        useState<PriceConfiguratorAnswers | null>(null);
    const [questAnsDialogOpen, setQuestAnsDialogOpen] = useState(false);
    const [initialQuestion, setInitialQuestion] =
        useState<PriceConfiguratorQuestions | null>(null);
    const [quoteData, setQuoteData] = useState<GridRowsProp>([]);

    //API Usestate//
    const apiRef = useGridApiRef();
    const [distributorTypes, setDistributorTypes] = useState<string[]>([]);

    //PDF Usestate//
    const [downloadingDataSheet, setDownloadingDataSheet] = useState<
        string | null
    >(null);

    const [pdfDialogOpen, setPdfDialogOpen] = useState(false);
    const [selectedMaterial, setSelectedMaterial] = useState<string | null>(
        null,
    );
    const [pdfBase64, setPdfBase64] = useState<string | null>(null);
    //END UseEffects--------------------------------//

    //DataGridPro//--------------------------------//
    //
    //DataGridPro Menu
    const handleRefresh = () => {
        setColumnSearchText('');
        setFilterModel({
            items: [],
        });
        setSearchColumn('product_Name');
        setSelectedConfiguratorAnswer(null);
    };
    const handleExpandAll = () => {
        setDetailPanelExpandedRowIds(quoteData?.map((row) => row?.id));
    };
    const handleCollapseAll = () => {
        setDetailPanelExpandedRowIds([]);
    };
    const handleFilterClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };
    const handleFilterClose = () => {
        setAnchorEl(null);
    };

    const handleColumnVisibilityChange = (
        field: keyof typeof columnVisibilityModel,
    ) => {
        setColumnVisibilityModel((prev) => ({
            ...prev,
            [field]: !prev[field],
        }));
    };

    const handleSearchColumnChange = (event: SelectChangeEvent) => {
        setSearchColumn(
            event.target.value as
                | 'distributor_Type'
                | 'product_Grouping'
                | 'product_Name'
                | 'material'
                | 'msrp'
                | 'discount_Percentage'
                | 'type_Level'
                | 'sales_Lead_Time'
                | 'available_Quantity'
                | 'sales_Published_Date',
        );
        setColumnSearchText('');
        setFilterModel({ items: [] });
    };

    const handleColumnSearch = (event: ChangeEvent<HTMLInputElement>) => {
        setColumnSearchText(event?.target?.value);
        setFilterModel({
            items: [
                {
                    field: searchColumn,
                    operator: 'contains',
                    value: event?.target?.value,
                },
            ],
        });
    };

    //DataGridPro Data//
    const filteredQuoteData = React.useMemo(() => {
        if (!selectedConfiguratorAnswer) {
            return quoteData.filter((row) => {
                const pg = row.product_Grouping?.toLowerCase();
                return (
                    pg &&
                    ![
                        '',
                        'mechatronics',
                        // 'stand-alone products',
                        // 'welding-cutting',
                    ].includes(pg)
                );
            });
        }

        const answerProductParts = answerProducts
            .filter(
                (p) =>
                    p.Price_Configurator_Answer ===
                    selectedConfiguratorAnswer.Price_Configurator_Answer,
            )
            .map((ap) => ap.Part_Number);

        return quoteData.filter((row) =>
            answerProductParts.includes(row.material),
        );
    }, [
        quoteData,
        selectedConfiguratorAnswer,
        answerProducts,
        distributorTypes,
    ]);

    //DataGridPro Astetics//
    const handleDetailPanelExpandedRowIdsChange = useCallback<
        NonNullable<DataGridProProps['onDetailPanelExpandedRowIdsChange']>
    >((newIds: GridRowId[]) => {
        setDetailPanelExpandedRowIds(newIds);
    }, []);

    const getDetailPanelHeight: DataGridProProps['getDetailPanelHeight'] = () =>
        'auto';
    const categoryColorMap: Record<string, string> = {
        Mechatronics: '#2d98da', // Blue
        'Stand-Alone Products': '#26de81', // Green
        'Welding-Cutting': '#8854d0', // Purple
        CNC: '#0fb9b1', // Teal
        'Category 4': '#eb3b5a', // Red
        'Category 5': '#f7b731', // Yellow
        'Category 6': '#4b6584', // Gray
    };
    const columns: GridColDef[] = [
        {
            field: 'distributor_Type',
            headerName: 'Distribution Category',
            flex: 2,
        },
        {
            field: 'product_Grouping',
            headerName: 'Category',
            flex: 0.75,
            renderCell: (params) => {
                const backgroundColor =
                    categoryColorMap[params.value] || '#e0e0e0';

                return (
                    <Chip
                        label={params.value}
                        variant="filled"
                        size="small"
                        sx={{
                            fontWeight: 'bold',
                            color: '#ffffff',
                            backgroundColor,
                        }}
                    />
                );
            },
        },
        { field: 'product_Name', headerName: 'Product Name', flex: 2 },
        { field: 'material', headerName: 'Part Number', flex: 2.25 },
        {
            field: 'msrp',
            headerName: 'MSRP',
            flex: 1,
            align: 'right',
            valueFormatter: (params) =>
                new Intl.NumberFormat('en-US', {
                    style: 'currency',
                    currency: 'USD',
                }).format(params?.value),
        },
        {
            field: 'discount_Percentage',
            headerName: 'Discount %',
            flex: 0.75,
            align: 'right',
        },
        {
            field: 'type_Level',
            headerName: 'Distributor Price',
            flex: 1,
            align: 'right',
            valueFormatter: (params) =>
                new Intl.NumberFormat('en-US', {
                    style: 'currency',
                    currency: 'USD',
                }).format(params?.value),
        },
        {
            field: 'sales_Lead_Time',
            headerName: 'Lead Time',
            flex: 0.75,
            align: 'right',
        },
        {
            field: 'available_Quantity',
            headerName: 'Avail Qty',
            flex: 0.75,
            align: 'right',
        },
        {
            field: 'sales_Published_Date',
            headerName: 'Published Date',
            flex: 0.75,
            align: 'right',
        },
    ];
    const getDetailPanelContent: DataGridProProps['getDetailPanelContent'] = ({
        row,
    }) => (
        <Box
            sx={{
                p: 2,
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'space-between',
                backgroundColor: '#dedede',
            }}
        >
            <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                <Typography sx={{ fontSize: 14, fontWeight: 'bold' }}>
                    Description:
                </Typography>
                <Typography variant="caption">
                    {row?.description || 'No description available'}
                </Typography>
            </Box>
            <Divider orientation="vertical" flexItem aria-hidden />
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '10px',
                    marginLeft: '15px',
                    height: '100%',
                }}
            >
                <Tooltip title="Show Data Sheet" placement="top">
                    <Button
                        aria-label="Show Data Sheet"
                        onClick={() => {
                            setSelectedMaterial(row.material ?? '');

                            setPdfDialogOpen(true);
                        }}
                        color="primary"
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            minWidth: 0,
                            minHeight: 0,
                            padding: 1,
                            backgroundColor: '#f44336',
                            '&:hover': {
                                backgroundColor: '#d32f2f',
                            },
                        }}
                    >
                        <Typography
                            variant="caption"
                            sx={{
                                fontWeight: 'bold',
                                textAlign: 'center',
                                color: '#fff',
                            }}
                        >
                            Data
                        </Typography>
                        <Typography
                            variant="caption"
                            sx={{
                                fontWeight: 'bold',
                                textAlign: 'center',
                                color: '#fff',
                            }}
                        >
                            Sheet
                        </Typography>
                        <Visibility sx={{ marginTop: 0.5, color: '#fff' }} />
                    </Button>
                </Tooltip>
            </Box>
        </Box>
    );

    //END DataGridPro--------------------------------//

    //Price and Build//--------------------------------//
    //
    const handleConfiguratorAnswerSelect = (
        answer: PriceConfiguratorAnswers | null,
    ) => {
        setSelectedConfiguratorAnswer(answer);
    };

    //END Price and Build--------------------------------//

    //Datasheet - PDF Download//--------------------------------//
    //
    const handleDownloadCSV = () => {
        const visibleColumns = apiRef.current
            .getAllColumns()
            .filter(
                (col) =>
                    columnVisibilityModel[col.field] !== false &&
                    col.field !== '__check__' &&
                    col.field !== '__detail_panel_toggle__',
            );

        const visibleRowIds = gridFilteredSortedRowIdsSelector(
            apiRef.current.state,
        );

        const header = visibleColumns
            .map((col) => col.headerName || col.field)
            .join(',');

        const rows = Array.from(visibleRowIds).map((id) => {
            const row = apiRef.current.getRow(id);
            return visibleColumns
                .map((col) => {
                    const value = row[col.field];
                    if (typeof value === 'string' && value.includes(',')) {
                        return `"${value.replace(/"/g, '""')}"`;
                    }
                    return value ?? '';
                })
                .join(',');
        });

        const csvContent = [header, ...rows].join('\r\n');
        const blob = new Blob([csvContent], {
            type: 'text/csv;charset=utf-8;',
        });

        if ((window.navigator as any).msSaveOrOpenBlob) {
            (window.navigator as any).msSaveOrOpenBlob(
                blob,
                'Pricing_Information.csv',
            );
        } else {
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'Pricing_Information.csv');
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        }
    };

    const handleDownloadPdfFromBase64 = (
        base64String: string,
        filename = 'DataSheet.pdf',
    ) => {
        if (!base64String) {
            toast?.error('No data sheet available.');
            return;
        }
        const base64 = base64String.replace(
            /^data:application\/pdf;base64,/,
            '',
        );
        const byteCharacters = atob(base64);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: 'application/pdf' });

        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    //END Datasheet - Download PDF--------------------------------//

    //API//--------------------------------//
    //
    const globalState = useGlobalStore((state) => state);

    const fetchAllData = async () => {
        setLoadingDataGrid(true);
        try {
            // 🐶 Fetch Distributor Types

            //Uncomment when Moving to Dev
            const customerRoles = globalState?.customerRoles?.split(',') || [];

            const distributorTypesArray = customerRoles
                .map((role) => role.trim())
                .filter((type): type is string => !!type && type !== 'Unknown');

            //Used for Active Dev
            // const distributorTypesArray = [
            //     'Education - Material Handling',
            //     'Welding Products',
            //     'Swivellink Mounting Products',
            //     'Stronguard Products',
            //     'Conveyor Products',
            //     'Robot Accessories',
            //     'Packaging Equipment',
            //     'Education - Rockwell',
            //     'Education - Welding',
            // ];

            setDistributorTypes(distributorTypesArray);

            // 🐶 Fetch Discount Levels
            let distributorLevelsData: Array<CustomerDistributorLevels> = [];
            const discountLevelsData = globalState.customerId
                ? AuthGet(
                      'https://synthesis.aptmfg.com:7114/GetCustomerDistributorLevels',
                      { customerID: globalState.customerId },
                  )
                : Promise.resolve({ data: [] });

            // 🐶 Fetch Price List
            const priceListData = distributorTypesArray.map((distributorType) =>
                AuthGet('https://synthesis.aptmfg.com:7114/GetPriceList', {
                    distributorType,
                }),
            );

            const [discountLevelsRes, ...priceListResults] = await Promise.all([
                discountLevelsData,
                ...priceListData,
            ]);

            if (discountLevelsRes?.data?.length > 0) {
                distributorLevelsData = discountLevelsRes.data;
            }

            const priceListArray: Array<PriceList> = [];
            priceListResults.forEach((listRes, idNum) => {
                if (listRes?.data?.length > 0) {
                    listRes.data.forEach((item: PriceList) =>
                        priceListArray.push({
                            ...item,
                            distributor_Type: distributorTypesArray[idNum],
                        }),
                    );
                }
            });

            const formatDateMMDDYYYY = (
                dateString: string | null | undefined,
            ) => {
                if (!dateString) return '';
                const date = new Date(dateString);
                if (isNaN(date.getTime())) return '';
                const mm = String(date.getMonth() + 1).padStart(2, '0');
                const dd = String(date.getDate()).padStart(2, '0');
                const yyyy = date.getFullYear();
                return `${mm}/${dd}/${yyyy}`;
            };

            const mergedData = priceListArray.map((item, index) => {
                const distributorLevel = distributorLevelsData.find(
                    (level) =>
                        level.distributor_Type?.trim().toLowerCase() ===
                        item.distributor_Type?.trim().toLowerCase(),
                );
                const typeLevelSet = distributorLevel?.type_Level ?? 0;
                const msrpSet = item.msrp ?? 0;
                const discountedMultiplier = (100 - typeLevelSet) / 100;
                const discountedPrice = discountedMultiplier * msrpSet;
                const percentage =
                    distributorLevel?.type_Level !== undefined
                        ? `${distributorLevel.type_Level} %`
                        : '0 %';

                let leadTime = `${item.sales_Lead_Time} ${item.sales_Lead_Time_Unit}`;
                if (item.sales_Lead_Time === 0) leadTime = '';

                const publishedDate = formatDateMMDDYYYY(
                    item.sales_Published_Date?.toString(),
                );

                return {
                    id: item.material ? String(item.material) : `row-${index}`,
                    product_Name: item.product_Name,
                    product_Grouping: item.product_Grouping,
                    material: item.material,
                    description: item.description,
                    msrp: item.msrp,
                    sales_Lead_Time: leadTime,
                    sales_Published_Date: publishedDate,
                    available_Quantity: item.available_Quantity,
                    type_Level: discountedPrice.toFixed(2),
                    distributor_Type: item.distributor_Type,
                    discount_Percentage: percentage,
                };
            });

            const filteredMergedData = mergedData.filter(
                (item) =>
                    item.distributor_Type != null &&
                    distributorTypesArray.includes(
                        item.distributor_Type,

                        //Active Dev
                        //'Education - Material Handling',
                    ),
            );

            const uniqueMaterial = filteredMergedData.filter(
                (item, index, self) =>
                    index ===
                    self.findIndex((t) => t.material === item.material),
            );

            setQuoteData(uniqueMaterial);
        } catch (error) {
            toast.error('Error fetching data');
        } finally {
            setLoadingDataGrid(false);
        }
    };

    useEffect(() => {
        if (!quoteData.length) fetchAllData();
    }, []);

    useEffect(() => {
        if (pdfDialogOpen && selectedMaterial) {
            const fetchPdf = async () => {
                setDownloadingDataSheet(selectedMaterial);
                try {
                    const res = await AuthGet(
                        'https://synthesis.aptmfg.com:7114/Requests/ReportService/GetMaterialDataSheet',
                        { partNumber: selectedMaterial },
                    );
                    const data: string = res?.data ?? '';
                    if (data.length > 0) {
                        setPdfBase64(data);
                    } else {
                        setPdfBase64(null);
                        toast?.error(
                            'No data sheet available for this material.',
                        );
                    }
                } catch (error) {
                    setPdfBase64(null);
                    toast?.error('Error fetching data sheet');
                } finally {
                    setDownloadingDataSheet(null);
                }
            };
            fetchPdf();
        } else {
            setPdfBase64(null);
        }
    }, [pdfDialogOpen, selectedMaterial]);

    useEffect(() => {
        const fetchQuest = async () => {
            try {
                const resQuest = AuthGet(
                    'https://synthesis.aptmfg.com:7114/PriceConfigurator/PriceConfiguratorQuestion/GetQuestions',
                );
                const question: Array<PriceConfiguratorQuestions> =
                    await resQuest?.then((res) =>
                        res.data?.map((quest: PriceConfiguratorQuestions) => ({
                            Price_Configurator_Question:
                                quest?.Price_Configurator_Question,
                            Price_Configurator_Answer:
                                quest?.Price_Configurator_Answer,
                            Distributor_Type: quest?.Distributor_Type,
                            Question: quest?.Question,
                            Active: quest?.Active,
                        })),
                    );
                setQuestions(question);
            } catch (err: any) {
                toast?.error('Error Fetching');
            }
        };
        fetchQuest();
    }, []);

    useEffect(() => {
        const fetchAns = async () => {
            try {
                const resAns = AuthGet(
                    'https://synthesis.aptmfg.com:7114/PriceConfigurator/PriceConfiguratorAnswer/GetAnswers',
                );
                const answer: Array<PriceConfiguratorAnswers> =
                    await resAns?.then((res) =>
                        res.data?.map((ans: PriceConfiguratorAnswers) => ({
                            Price_Configurator_Answer:
                                ans?.Price_Configurator_Answer,
                            Price_Configurator_Question:
                                ans?.Price_Configurator_Question,
                            Answer: ans?.Answer,
                            Active: ans?.Active,
                        })),
                    );
                setAnswers(answer);
            } catch (err: any) {
                toast?.error('Error Fetching');
            }
        };
        fetchAns();
    }, []);

    useEffect(() => {
        const fetchAnsProducts = async () => {
            try {
                const resAnsProd = AuthGet(
                    'https://synthesis.aptmfg.com:7114/PriceConfigurator/PriceConfiguratorAnswerProduct/GetProducts',
                );
                const product: Array<PriceConfiguratorAnswerProducts> =
                    await resAnsProd?.then((res) =>
                        res.data?.map(
                            (prod: PriceConfiguratorAnswerProducts) => ({
                                Price_Configurator_Answer_Product:
                                    prod?.Price_Configurator_Answer_Product,
                                Price_Configurator_Answer:
                                    prod?.Price_Configurator_Answer,
                                Part_Number: prod?.Part_Number,
                            }),
                        ),
                    );
                setAnswerProducts(product);
            } catch (err: any) {
                toast?.error('Error Fetching');
            }
        };
        fetchAnsProducts();
    }, []);

    //END API--------------------------------//

    return (
        <ThemeProvider theme={defaultTheme}>
            <Box
                sx={{
                    padding: '30px 10px 10px 10px',
                    maxWidth: '100vp',
                    display: 'flex',
                    flexDirection: 'column',
                }}
            >
                {/*{OldDataGrid()}*/}

                <Card sx={{ padding: '5px', margin: '5px' }}>
                    <Grid
                        container
                        style={{
                            padding: '10px 10px 10px 20px',
                            backgroundColor: '#f44336',
                        }}
                    >
                        <Grid
                            item
                            sx={{
                                color: '#fff',
                                fontWeight: 'bold',
                                fontSize: '20px',
                            }}
                        >
                            <div>Pricing Information</div>
                        </Grid>
                    </Grid>
                    <CardContent>
                        <Box
                            sx={{
                                height: '75vh',
                                width: '100%',
                                paddingBottom: '35px',
                            }}
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    gap: 1,
                                    marginBottom: 2,
                                    flexWrap: 'nowrap',
                                    overflowX: 'auto',
                                }}
                            >
                                {/* Column Select Dropdown */}
                                <FormControl
                                    size="small"
                                    sx={{ minWidth: 160 }}
                                >
                                    <Select
                                        aria-label="Column View Edit"
                                        role="Select Form"
                                        value={searchColumn}
                                        onChange={handleSearchColumnChange}
                                    >
                                        <MenuItem value="distributor_Type">
                                            Distribution Category
                                        </MenuItem>
                                        <MenuItem value="product_Grouping">
                                            Category
                                        </MenuItem>
                                        <MenuItem value="product_Name">
                                            Product Name
                                        </MenuItem>
                                        <MenuItem value="material">
                                            Part Number
                                        </MenuItem>
                                        <MenuItem value="msrp">MSRP</MenuItem>
                                        <MenuItem value="type_Level">
                                            Distributor Price
                                        </MenuItem>
                                        <MenuItem value="sales_Lead_Time">
                                            Lead Time
                                        </MenuItem>
                                        <MenuItem value="available_Quantity">
                                            Avail Qty
                                        </MenuItem>
                                    </Select>
                                </FormControl>
                                {/* Searchbar */}
                                <TextField
                                    aria-label="Table Search Bar"
                                    role="Search Bar"
                                    size="small"
                                    variant="outlined"
                                    placeholder={`Search by ${searchColumn.replace(
                                        '_',
                                        ' ',
                                    )}`}
                                    value={searchColumnText}
                                    onChange={handleColumnSearch}
                                    sx={{ minWidth: 250 }}
                                    InputProps={{
                                        startAdornment: (
                                            <IconButton aria-hidden>
                                                <Search />
                                            </IconButton>
                                        ),
                                    }}
                                />
                                {/* Filter Refresh Button */}
                                <Tooltip
                                    title="Remove All Filters"
                                    placement="top"
                                >
                                    <IconButton
                                        aria-label="Remove All Filters"
                                        role="Button"
                                        size="medium"
                                        sx={{ color: '#f44336' }}
                                        onClick={handleRefresh}
                                    >
                                        <SettingsBackupRestoreOutlined fontSize="inherit" />
                                    </IconButton>
                                </Tooltip>
                                {/* Filter Columns Button */}
                                <Tooltip title="Filter Columns" placement="top">
                                    <IconButton
                                        aria-label="Filter Table Columns"
                                        role="Button"
                                        size="medium"
                                        sx={{ color: '#f44336' }}
                                        onClick={handleFilterClick}
                                    >
                                        <ViewWeekOutlined fontSize="inherit" />
                                    </IconButton>
                                </Tooltip>
                                <Menu
                                    anchorEl={anchorEl}
                                    open={openAnchor}
                                    onClose={handleFilterClose}
                                >
                                    {Object.keys(columnVisibilityModel).map(
                                        (field) => {
                                            const columnHeader =
                                                columns.find(
                                                    (col) =>
                                                        col.field === field,
                                                )?.headerName || field;
                                            return (
                                                <MenuItem key={field}>
                                                    <FormControlLabel
                                                        control={
                                                            <Checkbox
                                                                checked={
                                                                    columnVisibilityModel[
                                                                        field as keyof typeof columnVisibilityModel
                                                                    ]
                                                                }
                                                                onChange={() =>
                                                                    handleColumnVisibilityChange(
                                                                        field as keyof typeof columnVisibilityModel,
                                                                    )
                                                                }
                                                            />
                                                        }
                                                        label={columnHeader}
                                                    />
                                                </MenuItem>
                                            );
                                        },
                                    )}
                                </Menu>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        marginLeft: 'auto',
                                    }}
                                ></Box>

                                <Button
                                    variant="contained"
                                    sx={{ color: 'white' }}
                                    onClick={() => {
                                        const firstQuestion = questions[0];
                                        if (firstQuestion) {
                                            setInitialQuestion(firstQuestion);
                                            setQuestAnsDialogOpen(true);
                                        }
                                    }}
                                >
                                    <Typography
                                        variant="body2"
                                        sx={{ fontWeight: 'bold' }}
                                    >
                                        Build and Price
                                    </Typography>
                                </Button>

                                {initialQuestion && (
                                    <PriceDialog
                                        open={questAnsDialogOpen}
                                        onClose={() =>
                                            setQuestAnsDialogOpen(false)
                                        }
                                        questions={questions}
                                        answers={answers}
                                        initialQuestion={initialQuestion}
                                        customerRoles={distributorTypes}
                                        onConfiguratorAnswerSelect={
                                            handleConfiguratorAnswerSelect
                                        }
                                    />
                                )}

                                {/* Expand All Button */}
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        marginLeft: 'auto',
                                    }}
                                >
                                    <Tooltip title="Expand All" placement="top">
                                        <IconButton
                                            aria-label="Expand All"
                                            role="Button"
                                            size="medium"
                                            sx={{ color: '#f44336' }}
                                            onClick={handleExpandAll}
                                        >
                                            <ArrowCircleDownOutlined fontSize="inherit" />
                                        </IconButton>
                                    </Tooltip>
                                    {/* Collapse All Buttons */}
                                    <Tooltip
                                        title="Collapse All"
                                        placement="top"
                                    >
                                        <IconButton
                                            aria-label="Collapse All"
                                            role="Button"
                                            size="medium"
                                            sx={{ color: '#f44336' }}
                                            onClick={handleCollapseAll}
                                        >
                                            <ArrowCircleUpOutlined fontSize="inherit" />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip
                                        title="Download CSV File of Current Table"
                                        placement="top"
                                    >
                                        <IconButton
                                            aria-label="Download CSV File"
                                            role="Button"
                                            size="medium"
                                            sx={{ color: '#f44336' }}
                                            onClick={handleDownloadCSV}
                                        >
                                            <Download fontSize="inherit" />
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                            </Box>

                            <DataGridPro
                                apiRef={apiRef}
                                loading={loadingDataGrid}
                                sortModel={sortModel}
                                onSortModelChange={(newSortModel) =>
                                    setSortModel(newSortModel)
                                }
                                rows={filteredQuoteData}
                                columns={columns}
                                autosizeOnMount
                                filterModel={filterModel}
                                onFilterModelChange={(newFilterModel) =>
                                    setFilterModel(newFilterModel)
                                }
                                getDetailPanelContent={getDetailPanelContent}
                                getDetailPanelHeight={getDetailPanelHeight}
                                detailPanelExpandedRowIds={
                                    detailPanelExpandedRowIds
                                }
                                onDetailPanelExpandedRowIdsChange={
                                    handleDetailPanelExpandedRowIdsChange
                                }
                                columnVisibilityModel={columnVisibilityModel}
                                onColumnVisibilityModelChange={(newModel) =>
                                    setColumnVisibilityModel(newModel)
                                }
                                sx={{
                                    '& .MuiDataGrid-columnHeaders': {
                                        fontWeight: 'bold',
                                        backgroundColor: '#f44336',
                                        color: '#fff',
                                    },
                                    '& .MuiDataGrid-columnHeaderTitle': {
                                        fontWeight: 'bold',
                                    },
                                    '& .MuiDataGrid-row:nth-of-type(even)': {
                                        backgroundColor: '#f9f9f9',
                                    },
                                    '& .MuiDataGrid-row:nth-of-type(odd)': {
                                        backgroundColor: '#ffffff',
                                    },
                                    '& .MuiDataGrid-cell:focus': {
                                        outline: 'none',
                                    },
                                    '& .MuiDataGrid-cell:focus-within': {
                                        outline: 'none',
                                    },
                                }}
                            />
                        </Box>
                    </CardContent>
                </Card>
            </Box>

            <Dialog
                open={pdfDialogOpen}
                onClose={() => setPdfDialogOpen(false)}
                maxWidth="md"
                fullWidth
            >
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        height: '80vh',
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            p: 1,
                            color: '#FFFFFF',
                            background: '#f44336',
                        }}
                    >
                        <Box>
                            <Tooltip title="Back" placement="top">
                                <IconButton
                                    aria-label="Back"
                                    role="button"
                                    sx={{ color: '#FFFFFF' }}
                                    onClick={() => setPdfDialogOpen(false)}
                                >
                                    <KeyboardBackspace />
                                </IconButton>
                            </Tooltip>
                        </Box>
                        <Box>
                            <Typography
                                variant="h5"
                                sx={{ fontWeight: 'bold' }}
                            >
                                {selectedMaterial} Data Sheet
                            </Typography>
                        </Box>
                        <Box>
                            <Tooltip
                                title="Download PDF of Data Sheet"
                                placement="top"
                            >
                                <IconButton
                                    aria-label="Download Data Sheet"
                                    sx={{ color: '#FFFFFF' }}
                                    onClick={() => {
                                        if (pdfBase64) {
                                            handleDownloadPdfFromBase64(
                                                pdfBase64,
                                                `${selectedMaterial}_DataSheet.pdf`,
                                            );
                                        }
                                    }}
                                    disabled={!pdfBase64}
                                >
                                    <Download />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    </Box>
                    <Box
                        sx={{
                            flex: 1,
                            overflow: 'auto',
                            p: 2,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        {downloadingDataSheet === selectedMaterial ? (
                            <CircularProgress
                                size={48}
                                thickness={5}
                                sx={{ color: '#f44336' }}
                            />
                        ) : pdfBase64 ? (
                            <iframe
                                title="PDF Preview"
                                src={`data:application/pdf;base64,${pdfBase64.replace(
                                    /^data:application\/pdf;base64,/,
                                    '',
                                )}`}
                                width="100%"
                                height="100%"
                                style={{
                                    minHeight: '70vh',
                                    border: 'none',
                                }}
                            />
                        ) : (
                            <Typography align="center" sx={{ mt: 4 }}>
                                PDF Failed to load.
                            </Typography>
                        )}
                    </Box>
                </Box>
            </Dialog>
        </ThemeProvider>
    );
};
